import type { AxiosInstance } from 'axios';
import type {
  CtdEnsemble,
  CtdEnsembleVersion,
  CtdEnsembleModel,
  CtdEnsembleCreateRequest,
  CtdEnsembleUpdateRequest,
  CtdEnsembleVersionCreateRequest,
  CtdEnsembleVersionUpdateRequest,
  CtdEnsembleVersionPublishRequest,
  CtdEnsemblesResponse,
  CtdCtdEnsembleVersionsResponse,
  AssemblyMode,
} from '../../types/CuratedEnsembles';
import {
  waitForResourceCreation,
  waitForResourceDeletion,
  waitForResourceUpdate,
  waitForFieldValue
} from '../../utils/apiRetryUtils';

export const CtdEnsembleService = (api: AxiosInstance) => {
  const service = {
  
  // Get all ensembles with optional filtering (includes versions by default)
  getEnsembles: async (params?: {
    domainId?: string;
    outcomeId?: string;
    assemblyMode?: AssemblyMode;
    pageNumber?: number;
    pageSize?: number;
    sort?: string;
    search?: string;
    includeVersions?: boolean;
    hasPublishedVersion?: boolean;
  }) => {
    try {
      const queryParams: Record<string, string | boolean> = {
        includeVersions: params?.includeVersions !== false // Default to true
      };

      if (params?.domainId) queryParams.domainId = params.domainId;
      if (params?.outcomeId) queryParams.outcomeId = params.outcomeId;
      if (params?.assemblyMode) queryParams.assemblyMode = params.assemblyMode;
      if (params?.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber.toString();
      if (params?.pageSize !== undefined) queryParams.pageSize = params.pageSize.toString();
      if (params?.hasPublishedVersion !== undefined) queryParams.hasPublishedVersion = params.hasPublishedVersion.toString();
      // if (params?.sort) queryParams.sort = params.sort;
      // if (params?.search) queryParams.search = params.search;

      // Log API call details
      const fullUrl = `${api.defaults.baseURL}/ctd-ensembles`;
      const response = await api.get<CtdEnsemblesResponse>('/ctd-ensembles', { params: queryParams });
      console.log('[getEnsembles] API Call:', {
        url: fullUrl,
        params: queryParams,
        inputParams: params,
        data: response.data
      });
      return response.data;
    } catch (error) {
      console.error('Error getting ctd-ensembles:', error);
      throw error;
    }
  },

  // Get a single ensemble by ID
  getCtdEnsemble: async (ctdEnsembleId: string, includeVersions: boolean = true) => {
    try {
      const response = await api.get<CtdEnsemble>(`/ctd-ensembles/${ctdEnsembleId}`, {
        params: { includeVersions }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting ctd-ensemble:', error);
      throw error;
    }
  },

  // Create a new ctd-ensemble
  createCtdEnsemble: async (data: CtdEnsembleCreateRequest) => {
    try {
      const response = await api.post<CtdEnsemble>('/ctd-ensembles', data);

      // Wait for ctd-ensemble to be available in OpenSearch using the getCtdEnsemble method
      const verifiedCtdEnsemble = await waitForResourceCreation(
        () => service.getCtdEnsemble(response.data.id, false), // Use the service's own getCtdEnsemble method
        undefined, // Use default retry config
        (ctdEnsemble) => {
          // Validate that the ctd-ensemble has the expected fields
          return !!(ctdEnsemble && ctdEnsemble.id === response.data.id &&
                    ctdEnsemble.domainId === data.domainId &&
                    ctdEnsemble.outcomeId === data.outcomeId);
        }
      );

      return verifiedCtdEnsemble || response.data;
    } catch (error) {
      console.error('Error creating ctd-ensemble:', error);
      throw error;
    }
  },

  // Update an existing ctd-ensemble
  updateCtdEnsemble: async (ctdEnsembleId: string, data: CtdEnsembleUpdateRequest) => {
    try {
      const response = await api.put<CtdEnsemble>(`/ctd-ensembles/${ctdEnsembleId}`, data);

      // Wait for update to propagate (check if provided fields match)
      const verifiedEnsemble = await waitForResourceUpdate(
        () => service.getCtdEnsemble(ctdEnsembleId, false),
        data, // Check that the updated fields match
        'lastUpdated'
      );

      return verifiedEnsemble || response.data;
    } catch (error) {
      console.error('Error updating ctd-ensemble:', error);
      throw error;
    }
  },

  // Delete an ensemble
  deleteCtdEnsemble: async (ctdEnsembleId: string) => {
    try {
      await api.delete(`/ctd-ensembles/${ctdEnsembleId}`);

      // Wait for deletion to propagate in OpenSearch
      const deleted = await waitForResourceDeletion(
        () => api.get(`/ctd-ensembles/${ctdEnsembleId}`)
      );

      return deleted;
    } catch (error) {
      console.error('Error deleting ctd-ensemble:', error);
      throw error;
    }
  },

  // Get all versions for an ensemble
  getCtdCtdEnsembleVersions: async (ctdEnsembleId: string, status?: string) => {
    try {
      const params = status ? { status } : {};
      const response = await api.get<CtdCtdEnsembleVersionsResponse>(
        `/ctd-ensembles/${ctdEnsembleId}/versions`, 
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting ctd-ensemble versions:', error);
      throw error;
    }
  },

  // Get a single ensemble version
  getCtdCtdEnsembleVersion: async (ctdEnsembleId: string, versionId: string) => {
    try {
      const response = await api.get<CtdEnsembleVersion>(
        `/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting ctd-ensemble version:', error);
      throw error;
    }
  },

  // Create a new ensemble version
  createCtdCtdEnsembleVersion: async (ctdEnsembleId: string, data: CtdEnsembleVersionCreateRequest) => {
    try {
      const response = await api.post<CtdEnsembleVersion>(
        `/ctd-ensembles/${ctdEnsembleId}/versions`,
        data
      );

      // Wait for version to be available in OpenSearch
      const verifiedVersion = await waitForResourceCreation(
        () => service.getCtdCtdEnsembleVersion(ctdEnsembleId, response.data.id),
        undefined, // Use default retry config
        (version) => {
          // Validate that the version has the expected fields
          return !!(version && version.id === response.data.id &&
                    version.ctdEnsembleId === ctdEnsembleId &&
                    version.name === data.name);
        }
      );

      return verifiedVersion || response.data;
    } catch (error) {
      console.error('Error creating ctd-ensemble version:', error);
      throw error;
    }
  },

  // Update an ensemble version
  updateCtdCtdEnsembleVersion: async (
    ctdEnsembleId: string,
    versionId: string,
    data: CtdEnsembleVersionUpdateRequest
  ) => {
    try {
      const response = await api.put<CtdEnsembleVersion>(
        `/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}`,
        data
      );

      // Wait for update to propagate (check if provided fields match)
      const verifiedVersion = await waitForResourceUpdate(
        () => service.getCtdCtdEnsembleVersion(ctdEnsembleId, versionId),
        data, // Check that the updated fields match
        'lastUpdated'
      );

      return verifiedVersion || response.data;
    } catch (error) {
      console.error('Error updating ctd-ensemble version:', error);
      throw error;
    }
  },

  // Delete an ensemble version
  deleteCtdCtdEnsembleVersion: async (ctdEnsembleId: string, versionId: string) => {
    try {
      await api.delete(`/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}`);

      // Wait for deletion to propagate in OpenSearch
      const deleted = await waitForResourceDeletion(
        () => api.get(`/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}`)
      );

      return deleted;
    } catch (error) {
      console.error('Error deleting ctd-ensemble version:', error);
      throw error;
    }
  },

  // Set models for an ensemble version (replaces all existing models)
  setModelsOnCtdVersion: async (
    ctdEnsembleId: string,
    versionId: string,
    models: CtdEnsembleModel[]
  ) => {
    // Validation
    if (!ctdEnsembleId) {
      throw new Error('ctdEnsembleId is required');
    }
    if (!versionId) {
      throw new Error('versionId is required');
    }


    const requestBody = {
      models: models || []
    };

    try {
      const response = await api.post<CtdEnsembleVersion>(
        `/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}/models`,
        requestBody
      );
      return response.data;
    } catch (error) {
      const axiosErr = error as import('axios').AxiosError;
      console.error('Error setting models on ensemble version:', axiosErr.response?.data || error);
      throw error;
    }
  },

  // @deprecated Use setModelsOnCtdVersion instead - adds a single model (kept for backward compatibility)
  addModelToCtdVersion: async (
    ctdEnsembleId: string,
    versionId: string,
    modelId: string,
    modelVersionId: string
  ) => {
    try {
      // Note: This now replaces all models, so we need to fetch existing ones first
      const version = await service.getCtdCtdEnsembleVersion(ctdEnsembleId, versionId);
      const existingModels = version.models || [];
      const newModels = [...existingModels, { modelId, versionId: modelVersionId }];

      const response = await api.post<CtdEnsembleVersion>(
        `/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}/models`,
        {
          models: newModels
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding model to ensemble version:', error);
      throw error;
    }
  },

  // @deprecated The DELETE endpoint no longer exists - use setModelsOnCtdVersion instead
  removeModelFromCtdVersion: async (
    ctdEnsembleId: string,
    versionId: string,
    modelId: string,
    modelVersionId: string
  ) => {
    try {
      // Fetch existing models and filter out the one to remove
      const version = await service.getCtdCtdEnsembleVersion(ctdEnsembleId, versionId);
      const existingModels = version.models || [];
      const newModels = existingModels.filter(
        m => !(m.modelId === modelId && m.versionId === modelVersionId)
      );

      const response = await api.post<CtdEnsembleVersion>(
        `/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}/models`,
        {
          models: newModels
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing model from ensemble version:', error);
      throw error;
    }
  },

  // Publish an ensemble version
  publishCtdCtdEnsembleVersion: async (
    ctdEnsembleId: string,
    versionId: string,
    data: CtdEnsembleVersionPublishRequest
  ) => {
    try {
      const response = await api.post<CtdEnsembleVersion>(
        `/ctd-ensembles/${ctdEnsembleId}/versions/${versionId}/publish`,
        data
      );

      // Wait for the version status to become 'published'
      const publishedVersion = await waitForFieldValue(
        () => service.getCtdCtdEnsembleVersion(ctdEnsembleId, versionId),
        'status',
        'published'
      );

      return publishedVersion || response.data;
    } catch (error) {
      console.error('Error publishing ensemble version:', error);
      throw error;
    }
  },

  // Get EPicks for a specific ensemble
  getCtdEnsembleEPicks: async (
    ctdEnsembleId: string,
    params?: {
      startDate?: string;
      endDate?: string;
      pageNumber?: number;
      pageSize?: number;
    }
  ) => {
    try {
      const queryParams: Record<string, string> = {};
      if (params?.startDate) queryParams.startDate = params.startDate;
      if (params?.endDate) queryParams.endDate = params.endDate;
      if (params?.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber.toString();
      if (params?.pageSize !== undefined) queryParams.pageSize = params.pageSize.toString();

      const response = await api.get(
        `/ctd-ensembles/${ctdEnsembleId}/epicks`,
        { params: queryParams }
      );
      // console.log(response);
      return response.data;
    } catch (error) {
      console.error('Error getting ctd-ensemble epicks:', error);
      throw error;
    }
  }
  };

  return service;
};